// API Configuration Helper
// Centralized configuration for API endpoints

const BACKEND_TARGET = (process.env.REACT_APP_BACKEND_TARGET || 'production').toLowerCase().trim();
const TARGET_SUFFIX = BACKEND_TARGET === 'test' ? 'TEST' : BACKEND_TARGET === 'dev' ? 'DEV' : 'PROD';
const DEFAULT_BACKEND_HOST = 'https://agent.freshroad.com';

const pickEnvByTarget = (baseName, fallback = '') => {
  const direct = process.env[baseName];
  if (direct) return direct;
  const targeted = process.env[`${baseName}_${TARGET_SUFFIX}`];
  return targeted || fallback;
};

// Get API base URL (without /api suffix)
// Falls back to empty string for relative URLs
// Get API base URL (without /api suffix) - Used for Agent Server (3016)
export const getApiBaseUrl = () => {
  return pickEnvByTarget('REACT_APP_AGENT_API_URL', `${DEFAULT_BACKEND_HOST}:3016`);
};

// Get full API URL (with /api suffix) - Used for Auth Server (3015)
export const getApiUrl = () => {
  return pickEnvByTarget('REACT_APP_AUTH_API_URL', `${DEFAULT_BACKEND_HOST}:3015/api`);
};

// Get Twilio/Agent API base URL (port 3017)
export const getTwilioApiUrl = () => {
  return pickEnvByTarget('REACT_APP_TWILIO_API_URL', '') ||
    (getApiBaseUrl() ? `${getApiBaseUrl().replace(':3016', ':3017')}` : '');
};

const OUTBOUND_CALL_DEFAULT_PRODUCTION = 'https://agent.freshroad.com:3017';
const OUTBOUND_CALL_PORT = '3017';

/** Outbound call API base. Always uses port 3017 for agent.freshroad.com (never default 443). */
export const getOutboundCallBaseUrl = () => {
  let raw =
    pickEnvByTarget('REACT_APP_OUTBOUND_CALL_API_URL', '') ||
    pickEnvByTarget('REACT_APP_CALL_API_URL', '');
  // When no env set, use target default host/port. Also auto-default for known hosted frontends.
  if (!raw && typeof window !== 'undefined') {
    const host = window.location?.hostname || '';
    if (
      host === 'go.freshroad.com' ||
      host === 'connect.freshroad.com' ||
      host === 'agent.freshroad.com' ||
      host === 'app.freshroad.com' ||
      host === '35.93.184.135'
    ) {
      return OUTBOUND_CALL_DEFAULT_PRODUCTION;
    }
  }
  if (!raw) {
    return OUTBOUND_CALL_DEFAULT_PRODUCTION;
  }
  const trimmed = raw.replace(/\/$/, '');
  try {
    const u = new URL(trimmed);
    if (u.hostname === 'agent.freshroad.com') {
      const port = u.port && u.port !== '443' && u.port !== '80' ? u.port : OUTBOUND_CALL_PORT;
      return `${u.protocol}//${u.hostname}:${port}`;
    }
  } catch (_) {}
  return trimmed;
};

/** Freshroad Travel Pass API (/api/p3/*, backend/travel) on its own port, 3019. REACT_APP_TRAVEL_API_URL overrides. */
export const getTravelApiBaseUrl = () =>
  pickEnvByTarget('REACT_APP_TRAVEL_API_URL', '').replace(/\/$/, '') || `${DEFAULT_BACKEND_HOST}:3019`;

/** Full outbound-call URL; relative /outbound-call when no base (for dev proxy). */
export const getOutboundCallUrl = () => {
  const base = getOutboundCallBaseUrl();
  return base ? `${base}/outbound-call` : '/outbound-call';
};

/** WebSocket URL for the realtime transcript feed (same voice server as outbound, /live-transcript).
 *  Pass an ownerId to scope the feed to that user's calls. */
export const getLiveTranscriptWsUrl = (ownerId = null) => {
  const base = getOutboundCallBaseUrl(); // e.g. https://agent.freshroad.com:3017
  const wsBase = base.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  const q = ownerId ? `?ownerId=${encodeURIComponent(ownerId)}` : '';
  return `${wsBase}/live-transcript${q}`;
};

/**
 * V2 outbound-call URL. Routes the call through the V2 server (port 3015), whose
 * /outbound-call handler points Twilio at the refactored V2 Kokoro VAD WebSocket handler.
 * Keeps the legacy endpoint (getOutboundCallUrl → 3017) untouched.
 */
// The V2 voice server (backend/v2/server.js) runs on its own port (default 3018), separate from
// the auth/billing server on 3015.
const OUTBOUND_CALL_V2_PORT = '3018';

export const getOutboundCallUrlV2 = () => {
  // Explicit override: set REACT_APP_OUTBOUND_CALL_V2_URL to the base where backend/v2/server.js
  // is hosted (public, TLS, reachable by Twilio for the wss callback).
  const explicit = pickEnvByTarget('REACT_APP_OUTBOUND_CALL_V2_URL', '');
  if (explicit) return `${explicit.replace(/\/$/, '')}/api/outbound-call`;
  // Default: same host as the auth base, but on the V2 voice server's own port (3018).
  const authBase = getApiUrl().replace(/\/api\/?$/, '');
  try {
    const u = new URL(authBase);
    return `${u.protocol}//${u.hostname}:${OUTBOUND_CALL_V2_PORT}/api/outbound-call`;
  } catch (_) {
    return `${authBase}/api/outbound-call`;
  }
};

/** Logged-in dashboard user id (string) for attributing phone/browser calls; empty if unknown. */
export const getCurrentUserIdForCalls = () => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('user') : null;
    if (!raw) return '';
    const u = JSON.parse(raw);
    const id = u?.id ?? u?._id ?? u?.userId;
    if (id == null || id === '') return '';
    return String(id);
  } catch {
    return '';
  }
};

// Helper to construct WebSocket URL from base
const getWsBaseUrl = () => {
  const twilioUrl = getTwilioApiUrl();
  if (twilioUrl) {
    // Check if the current page is loaded over HTTPS
    const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';

    // If page is HTTPS, use wss://; otherwise use ws://
    // Also respect the base URL protocol if it's explicitly set
    if (isHttps || twilioUrl.startsWith('https://')) {
      return twilioUrl.replace(/^https?:\/\//, 'wss://');
    } else {
      return twilioUrl.replace(/^https?:\/\//, 'ws://');
    }
  }
  return '';
};

// Get Browser Voice WebSocket URL (Legacy)
export const getBrowserVoiceWsUrl = () => {
  return process.env.REACT_APP_BROWSER_VOICE_WS ||
    (getWsBaseUrl() ? `${getWsBaseUrl()}/browser-voice-agent` : '');
};

// Get Browser Voice Realtime WebSocket URL
export const getBrowserVoiceRealtimeWsUrl = () => {
  return process.env.REACT_APP_BROWSER_REALTIME_WS ||
    process.env.REACT_APP_BROWSER_VOICE_REALTIME_WS ||
    (getWsBaseUrl() ? `${getWsBaseUrl()}/browser-voice-agent-realtime` : '');
};

// Get Browser Voice PTT WebSocket URL
export const getBrowserVoicePttWsUrl = () => {
  return process.env.REACT_APP_BROWSER_PTT_WS ||
    (getWsBaseUrl() ? `${getWsBaseUrl()}/browser-voice-agent-ptt` : '');
};

// Get Browser Voice Kokoro PTT WebSocket URL
export const getBrowserVoiceKokoroWsUrl = () => {
  return process.env.REACT_APP_BROWSER_KOKORO_WS ||
    (getWsBaseUrl() ? `${getWsBaseUrl()}/browser-voice-agent-kokoro` : '');
};

// Get Browser Voice Kokoro VAD WebSocket URL
export const getBrowserVoiceKokoroVadWsUrl = () => {
  return process.env.REACT_APP_BROWSER_KOKORO_VAD_WS ||
    (getWsBaseUrl() ? `${getWsBaseUrl()}/browser-voice-agent-kokoro-vad` : '');
};

// Get Browser Voice Kokoro WS (WebSocket TTS) WebSocket URL
export const getBrowserVoiceKokoroWsTtsUrl = () => {
  return process.env.REACT_APP_BROWSER_KOKORO_WS_TTS ||
    (getWsBaseUrl() ? `${getWsBaseUrl()}/browser-voice-agent-kokoro-ws` : '');
};

// Get Browser Voice V2 WebSocket URL
export const getBrowserVoiceV2WsUrl = () => {
  return process.env.REACT_APP_BROWSER_V2_WS ||
    (getWsBaseUrl() ? `${getWsBaseUrl()}/browser-voice-agent-v2` : '');
};

// Get Realtime Token URL (HTTPS endpoint)
export const getRealtimeTokenUrl = () => {
  return pickEnvByTarget('REACT_APP_REALTIME_TOKEN_URL', '') ||
    (getTwilioApiUrl() ? `${getTwilioApiUrl()}/realtime-token` : '');
};

// Get base path for the application (for routing and static assets)
// Defaults to '/' (root) if not specified
// Set REACT_APP_BASE_PATH environment variable to override (e.g., '/FreshroadDashboard')
export const getBasePath = () => {
  return process.env.REACT_APP_BASE_PATH || '/';
};

